import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  touch: { minHeight: "44px" },
  field: { marginTop: "12px" },
});

/** Picks a locale the template has no version in yet, and the language to copy as a start. */
const AddLanguageDialog = ({
  open,
  locales,
  variants,
  defaultFrom,
  onCancel,
  onConfirm,
}) => {
  const classes = useStyles();
  const taken = useMemo(
    () => new Set((variants || []).map((v) => v.localeId.toLowerCase())),
    [variants],
  );
  const available = useMemo(
    () => (locales || []).filter((l) => !taken.has(l.code.toLowerCase())),
    [locales, taken],
  );
  const [localeId, setLocaleId] = useState("");
  const [from, setFrom] = useState("");

  useEffect(() => {
    if (!open) return;
    setLocaleId(available[0]?.code || "");
    setFrom(defaultFrom || variants?.[0]?.localeId || "");
  }, [open, available, defaultFrom, variants]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="mt-add-language-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="mt-add-language-title">Add a language</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {available.length
            ? "The new language starts as a copy of an existing one; translate it, then save."
            : "This template already has every configured language."}
        </DialogContentText>
        <TextField
          select
          fullWidth
          id="mt-add-language-locale"
          label="Language"
          variant="outlined"
          className={classes.field}
          value={localeId}
          onChange={(e) => setLocaleId(e.target.value)}
          disabled={!available.length}
        >
          {available.map((l) => (
            <MenuItem key={l.code} value={l.code}>
              {l.code} — {l.englishName || l.nativeName}
              {l.enabled ? "" : " (not offered to users yet)"}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          id="mt-add-language-from"
          label="Start from"
          variant="outlined"
          className={classes.field}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        >
          <MenuItem value="">
            <em>Empty</em>
          </MenuItem>
          {(variants || []).map((v) => (
            <MenuItem key={v.localeId} value={v.localeId}>
              {v.localeId}
              {v.active ? "" : " (deleted)"}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button className={classes.touch} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className={classes.touch}
          color="primary"
          variant="contained"
          disabled={!localeId}
          onClick={() => onConfirm({ localeId, from })}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddLanguageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  locales: PropTypes.array,
  variants: PropTypes.array,
  defaultFrom: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

AddLanguageDialog.defaultProps = {
  locales: [],
  variants: [],
  defaultFrom: "",
};

export default AddLanguageDialog;
